import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { CaretDown, CaretRight, Paperclip, Trash } from '@phosphor-icons/react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useT } from '@/i18n/LanguageContext';

/**
 * <AttachmentsField> — 添付（仕様 2.6 #14）。
 *
 * 任意項目なので既定で畳む。入力済みのときは「入力あり」の印を出して
 * 畳んだまま見落とさないようにする。ファイル実体の保存は呼び出し側の
 * `onUpload(file)` が持つ（保存先はドキュメントアプリ）。
 */
export const AttachmentsField = ({
  files = [], onChange, onUpload, testPrefix = 'attachments',
  accept = '.png,.jpg,.jpeg,.gif,.webp,.heic,.heif,.pdf',
}) => {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const add = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setUploading(true);
    try {
      const r = await onUpload(f);
      onChange([...(files || []), r]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('reg.attach.failed'));
    } finally { setUploading(false); }
  };

  return (
    <div className="rounded-lg border border-zinc-200" data-testid={`${testPrefix}-section`}>
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-zinc-600 hover:bg-zinc-50"
        data-testid={`${testPrefix}-toggle`}>
        {open ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
        {t('reg.attach')}
        {(files || []).length > 0 && (
          <span className="ml-1 rounded bg-teal-50 px-1.5 py-0.5 text-[10px] text-teal-700">
            {t('reg.attach.filled')}
          </span>
        )}
      </button>
      {open && (
        <div className="border-t border-zinc-200 p-3 space-y-3" data-testid={`${testPrefix}-body`}>
          <Label className="text-xs font-semibold text-zinc-600">{t('reg.attach')}</Label>
          <input ref={inputRef} type="file" className="hidden" accept={accept} onChange={add} />
          <div className="mt-1 flex items-center gap-2">
            <Button type="button" variant="outline" className="h-9" disabled={uploading}
              onClick={() => inputRef.current?.click()} data-testid={`${testPrefix}-btn`}>
              <Paperclip size={13} className="mr-1" />
              {uploading ? t('reg.attach.uploading') : t('reg.attach.add')}
            </Button>
            <span className="text-[10px] text-zinc-400">{t('reg.attach.limit')}</span>
          </div>
          {(files || []).length > 0 && (
            <ul className="mt-2 space-y-1" data-testid={`${testPrefix}-list`}>
              {files.map((f) => (
                <li key={f.file_id || f.document_id}
                  className="flex items-center gap-2 rounded border border-zinc-200 px-2 py-1 text-[11px]"
                  data-testid={`${testPrefix}-file-${f.file_id || f.document_id}`}>
                  <Paperclip size={11} className="shrink-0 text-zinc-400" />
                  <span className="flex-1 min-w-0 truncate text-zinc-700">{f.filename}</span>
                  <button type="button" className="text-zinc-400 hover:text-rose-600"
                    onClick={() => onChange((files || []).filter((x) => x !== f))}
                    title={t('reg.attach.remove')}>
                    <Trash size={11} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default AttachmentsField;
